import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import { ClipLoader } from "react-spinners";
import useApiPost from "../../hooks/PostData";
import { useAppDispatch } from "../../utils/hooks";
import { updateUserData } from "../../store/Slices/UserSlice";
import LoginLeftSections from "./LoginLeftSections";
import OtpInputField from "./OtpInputField";
import { useWebsiteSettings } from "../../store/api/useWebsiteSettings";
import { useTheme } from "../../context/ThemeProvider";
import {
  isEmailRequiredGate,
  setEmailRequiredGate,
} from "../../utils/emailGate";
import type { User } from "../../types/ResType";

type Step = "email" | "otp";

function continueAfterProfile(user: User, navigate: ReturnType<typeof useNavigate>) {
  if (
    user.first_name == "" ||
    user.last_name == "" ||
    user.user_name == "" ||
    user.gender == ""
  ) {
    navigate("/user-details");
  } else if (user.profile_pic == "") {
    navigate("/select-profile");
  } else {
    window.location.pathname = "/chat";
  }
}

export default function BindEmail() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { loading, postData } = useApiPost();
  const { data: websiteSettings } = useWebsiteSettings();
  const { theme } = useTheme();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");

  useEffect(() => {
    const token = Cookies.get("whoxa_web_token");
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }
    if (!isEmailRequiredGate()) {
      navigate("/chat", { replace: true });
    }
  }, [navigate]);

  async function sendBindEmail() {
    const value = email.trim().toLowerCase();
    if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error("Введите корректный email");
      return;
    }
    try {
      const res = await postData("users/bind-email", { email: value });
      if (res?.status) {
        if (res.data?.has_email && !res.data?.email_required) {
          setEmailRequiredGate(false);
          toast.success("Email уже привязан");
          navigate("/chat");
          return;
        }
        setEmail(value);
        setStep("otp");
        setOtp("");
        toast.success("Код отправлен на почту");
      } else {
        toast.error(res?.message || "Не удалось отправить код");
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Ошибка отправки";
      toast.error(msg);
    }
  }

  async function verifyBindEmail() {
    if (otp.length !== 6) {
      toast.error("Введите код из письма");
      return;
    }
    try {
      const res = await postData("users/verify-bind-email", {
        email: email.trim().toLowerCase(),
        otp,
      });
      if (res?.status) {
        setEmailRequiredGate(false);
        const user = res.data?.user as User | undefined;
        if (user) {
          dispatch(updateUserData(user));
        }
        toast.success("Email подтверждён");
        if (user) {
          continueAfterProfile(user, navigate);
        } else {
          window.location.pathname = "/chat";
        }
      } else {
        toast.error(res?.message || "Неверный код");
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.message || "Ошибка проверки";
      toast.error(msg);
    }
  }

  return (
    <div className="grid min-h-screen w-screen bg-white text-black lg:grid-cols-2">
      <LoginLeftSections />
      <div
        className="relative col-span-1 flex h-full w-full flex-col items-center justify-center bg-cover bg-center"
        style={{ backgroundImage: `url(${"/Home/Login_page.jpg"})` }}
      >
        <img
          className="absolute right-5 top-5 w-20 xl:w-28"
          src={
            theme === "dark"
              ? websiteSettings?.data?.web_logo_dark
              : websiteSettings?.data?.web_logo_light
          }
          alt=""
        />
        <div className="mx-auto w-[90%] max-w-[30rem] space-y-7 rounded-2xl p-5 shadow-[rgba(17,_17,_26,_0.1)_0px_0px_16px] lg:p-10 2xl:max-w-[35rem] 2xl:p-16">
          <div className="text-lg lg:text-2xl">
            Привяжите email
          </div>
          <p className="text-sm text-[#3A3A3A] lg:text-base">
            Чтобы пользоваться {websiteSettings?.data?.app_name ?? "приложением"},
            нужно указать и подтвердить email. Потом можно входить и по телефону, и по почте.
          </p>

          {step === "email" ? (
            <>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="email"
                placeholder="Email *"
                className="w-full rounded-lg border bg-[#F2F2F2] py-3 pl-5 text-black outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter") void sendBindEmail();
                }}
              />
              <button
                type="button"
                onClick={() => void sendBindEmail()}
                className="primary-gradient relative h-12 w-full overflow-hidden rounded-lg px-4 py-2 text-base font-medium outline-none lg:text-lg"
              >
                {loading ? (
                  <ClipLoader color="black" size={23} />
                ) : (
                  "Отправить код"
                )}
              </button>
            </>
          ) : (
            <>
              <div className="text-sm text-[#3A3A3A]">
                Код отправлен на{" "}
                <span className="font-bold">{email}</span>
                <button
                  type="button"
                  className="ml-2 text-themeColor underline"
                  onClick={() => {
                    setStep("email");
                    setOtp("");
                  }}
                >
                  Изменить
                </button>
              </div>
              <OtpInputField
                checkOtp={verifyBindEmail}
                setotpPerant={setOtp}
                externalOtp={otp}
              />
              <button
                type="button"
                onClick={() => void verifyBindEmail()}
                className="primary-gradient relative h-12 w-full overflow-hidden rounded-lg px-4 py-2 text-base font-medium outline-none lg:text-lg"
              >
                {loading ? (
                  <ClipLoader color="black" size={23} />
                ) : (
                  "Подтвердить"
                )}
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={() => void sendBindEmail()}
                className="w-full text-center text-sm text-themeColor underline"
              >
                Отправить код ещё раз
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
